// White-key modes of C major. Every mode uses ONLY the white keys,
// each simply starts on a different note.

// MIDI helper: middle C (C4) = 60
export const WHITE_KEYS = [
  { name: "C", midi: 48, octave: 3 },
  { name: "D", midi: 50, octave: 3 },
  { name: "E", midi: 52, octave: 3 },
  { name: "F", midi: 53, octave: 3 },
  { name: "G", midi: 55, octave: 3 },
  { name: "A", midi: 57, octave: 3 },
  { name: "B", midi: 59, octave: 3 },
  { name: "C", midi: 60, octave: 4 },
  { name: "D", midi: 62, octave: 4 },
  { name: "E", midi: 64, octave: 4 },
  { name: "F", midi: 65, octave: 4 },
  { name: "G", midi: 67, octave: 4 },
  { name: "A", midi: 69, octave: 4 },
  { name: "B", midi: 71, octave: 4 },
  { name: "C", midi: 72, octave: 5 },
];

// Black keys map to the white key they sit AFTER (for layout positioning)
export const BLACK_KEYS = [
  { name: "C#", midi: 49, after: 48 },
  { name: "D#", midi: 51, after: 50 },
  { name: "F#", midi: 54, after: 53 },
  { name: "G#", midi: 56, after: 55 },
  { name: "A#", midi: 58, after: 57 },
  { name: "C#", midi: 61, after: 60 },
  { name: "D#", midi: 63, after: 62 },
  { name: "F#", midi: 66, after: 65 },
  { name: "G#", midi: 68, after: 67 },
  { name: "A#", midi: 70, after: 69 },
];

// Given a root midi, return the 8 white-key midis of that mode (root -> octave up)
const scaleFromRoot = (root) =>
  WHITE_KEYS.filter((k) => k.midi >= root && k.midi <= root + 12).map((k) => k.midi);

export const MODES = [
  {
    id: "ionian",
    name: "Ionian",
    root: 48,
    rootName: "C",
    alias: "The Major Scale",
    mood: "Happy, bright, resolved",
    color: "#FF7A70",
    formula: ["W", "W", "H", "W", "W", "W", "H"],
    scale: scaleFromRoot(48),
    description:
      "Your home base: the plain major scale. Play the white keys from one C to the next. Learn this and you've basically learned all the others.",
    songs: ["Let It Be — The Beatles", "Someone Like You — Adele", "Perfect — Ed Sheeran"],
  },
  {
    id: "dorian",
    name: "Dorian",
    root: 50,
    rootName: "D",
    alias: "The Cool Minor",
    mood: "Jazzy, hopeful, groovy",
    color: "#66AEFF",
    formula: ["W", "H", "W", "W", "W", "H", "W"],
    scale: scaleFromRoot(50),
    description:
      "Same white keys — just start on D. A minor sound with a hopeful lift. Big in funk, soul and jazz.",
    songs: ["Mad World — Gary Jules", "So What — Miles Davis", "Get Lucky — Daft Punk"],
  },
  {
    id: "phrygian",
    name: "Phrygian",
    root: 52,
    rootName: "E",
    alias: "The Spanish One",
    mood: "Dark, exotic, dramatic",
    color: "#66AEFF",
    formula: ["H", "W", "W", "W", "H", "W", "W"],
    scale: scaleFromRoot(52),
    description:
      "Same keys, start on E. A dark, Spanish/flamenco feel — nothing new to learn.",
    songs: ["Wherever I May Roam — Metallica", "Flamenco progressions", "War — Joe Satriani"],
  },
  {
    id: "lydian",
    name: "Lydian",
    root: 53,
    rootName: "F",
    alias: "The Dreamer",
    mood: "Floating, magical, cinematic",
    color: "#34C759",
    formula: ["W", "W", "W", "H", "W", "W", "H"],
    scale: scaleFromRoot(53),
    description:
      "Same keys, start on F. Dreamy and magical — the sound of movie wonder.",
    songs: ["The Simpsons Theme", "Flying Theme — E.T.", "Dreams — Fleetwood Mac"],
  },
  {
    id: "mixolydian",
    name: "Mixolydian",
    root: 55,
    rootName: "G",
    alias: "The Rocker",
    mood: "Bluesy, confident, upbeat",
    color: "#FF9500",
    formula: ["W", "W", "H", "W", "W", "H", "W"],
    scale: scaleFromRoot(55),
    description:
      "Same keys, start on G. Upbeat with a bluesy edge — the heart of rock and folk.",
    songs: ["Sweet Home Alabama", "Royals — Lorde", "Norwegian Wood — The Beatles"],
  },
  {
    id: "aeolian",
    name: "Aeolian",
    root: 57,
    rootName: "A",
    alias: "The Natural Minor",
    mood: "Sad, emotional, powerful",
    color: "#AF52DE",
    formula: ["W", "H", "W", "W", "H", "W", "W"],
    scale: scaleFromRoot(57),
    description:
      "Same keys, start on A. The classic sad sound behind countless ballads.",
    songs: ["Losing My Religion — R.E.M.", "Numb — Linkin Park", "Stairway to Heaven"],
  },
  {
    id: "locrian",
    name: "Locrian",
    root: 59,
    rootName: "B",
    alias: "The Rare Rebel",
    mood: "Tense, unstable, mysterious",
    color: "#5856D6",
    formula: ["H", "W", "W", "H", "W", "W", "W"],
    scale: scaleFromRoot(59),
    description:
      "Same keys, start on B. Tense and unusual — rarely used, but it completes the set.",
    songs: ["Army of Me — Björk", "YYZ — Rush (sections)", "Experimental jazz & metal"],
  },
];

// The 6 core diatonic triads of C major — again, ALL white keys.
// Stack every other white note from the root. 2–6 of these = thousands of pop songs.
export const TRIADS = [
  {
    id: "c-major",
    name: "C Major",
    numeral: "I",
    quality: "Major",
    rootName: "C",
    root: 48,
    notes: [48, 52, 55], // C3 E3 G3
    color: "#FF7A70",
    description:
      "C, E, G. The 'home' chord — bright and settled. Most songs start or end here.",
  },
  {
    id: "d-minor",
    name: "D Minor",
    numeral: "ii",
    quality: "Minor",
    rootName: "D",
    root: 50,
    notes: [50, 53, 57], // D3 F3 A3
    color: "#66AEFF",
    description:
      "D, F, A. Soft and a little sad. Adds gentle movement.",
  },
  {
    id: "e-minor",
    name: "E Minor",
    numeral: "iii",
    quality: "Minor",
    rootName: "E",
    root: 52,
    notes: [52, 55, 59], // E3 G3 B3
    color: "#66AEFF",
    description:
      "E, G, B. Calm and thoughtful — sits between the bright and dark chords.",
  },
  {
    id: "f-major",
    name: "F Major",
    numeral: "IV",
    quality: "Major",
    rootName: "F",
    root: 53,
    notes: [53, 57, 60], // F3 A3 C4
    color: "#34C759",
    description:
      "F, A, C. The big, uplifting 'lift' chord. Pair it with C, G and Am and you've got the famous 4-chord pop formula.",
  },
  {
    id: "g-major",
    name: "G Major",
    numeral: "V",
    quality: "Major",
    rootName: "G",
    root: 55,
    notes: [55, 59, 62], // G3 B3 D4
    color: "#FF9500",
    description:
      "G, B, D. The 'push' chord that pulls you back home to C. In almost every chorus.",
  },
  {
    id: "a-minor",
    name: "A Minor",
    numeral: "vi",
    quality: "Minor",
    rootName: "A",
    root: 57,
    notes: [57, 60, 64], // A3 C4 E4
    color: "#AF52DE",
    description:
      "A, C, E. The 'sad but pretty' chord at the heart of pop.",
  },
];

// Iconic progressions built from only these 6 chords
export const PROGRESSIONS = [
  {
    name: "The 4-Chord Song",
    chords: ["C Major", "G Major", "A Minor", "F Major"],
    numerals: "I – V – vi – IV",
    songs: "Let It Be, Someone Like You, With or Without You, and hundreds more",
  },
  {
    name: "The Sensitive Ballad",
    chords: ["A Minor", "F Major", "C Major", "G Major"],
    numerals: "vi – IV – I – V",
    songs: "Grenade, Africa, Save Tonight",
  },
  {
    name: "The Doo-Wop",
    chords: ["C Major", "A Minor", "F Major", "G Major"],
    numerals: "I – vi – IV – V",
    songs: "Stand By Me, Every Breath You Take",
  },
];

// Guided practice routines. These use ONLY generic diatonic chord progressions
// (common musical building blocks, not copyrightable) — no melodies or audio
// from any recording or artist are reproduced anywhere in this app.
export const PRACTICE_ROUTINES = [
  {
    id: "pop-anthem",
    name: "The Pop Anthem",
    numerals: "I – V – vi – IV",
    subtitle: "The most-used progression in modern pop.",
    steps: ["c-major", "g-major", "a-minor", "f-major"],
  },
  {
    id: "sensitive-ballad",
    name: "The Sensitive Ballad",
    numerals: "vi – IV – I – V",
    subtitle: "A heartfelt, emotional loop.",
    steps: ["a-minor", "f-major", "c-major", "g-major"],
  },
  {
    id: "doo-wop",
    name: "The 50s Doo-Wop",
    numerals: "I – vi – IV – V",
    subtitle: "Timeless, nostalgic and warm.",
    steps: ["c-major", "a-minor", "f-major", "g-major"],
  },
  {
    id: "emotional-loop",
    name: "The Emotional Loop",
    numerals: "vi – iii – IV – I",
    subtitle: "Reflective and cinematic.",
    steps: ["a-minor", "e-minor", "f-major", "c-major"],
  },
];

export const noteName = (midi) => {
  const names = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
  const octave = Math.floor(midi / 12) - 1;
  return `${names[midi % 12]}${octave}`;
};

// The 6 core diatonic triads of G major (for the "2nd Practice" — same patterns, key of G).
export const TRIADS_G = [
  { id: "g", name: "G Major", numeral: "I", quality: "Major", rootName: "G", notes: [55, 59, 62], color: "#FF9500" },
  { id: "am", name: "A Minor", numeral: "ii", quality: "Minor", rootName: "A", notes: [57, 60, 64], color: "#66AEFF" },
  { id: "bm", name: "B Minor", numeral: "iii", quality: "Minor", rootName: "B", notes: [59, 62, 66], color: "#66AEFF" },
  { id: "c", name: "C Major", numeral: "IV", quality: "Major", rootName: "C", notes: [60, 64, 67], color: "#34C759" },
  { id: "d", name: "D Major", numeral: "V", quality: "Major", rootName: "D", notes: [62, 66, 69], color: "#FF7A70" },
  { id: "em", name: "E Minor", numeral: "vi", quality: "Minor", rootName: "E", notes: [64, 67, 71], color: "#AF52DE" },
];

// Same progression patterns as the C routines, transposed to the key of G.
export const PRACTICE_ROUTINES_G = [
  { id: "pop-anthem", numerals: "I – V – vi – IV", steps: ["g", "d", "em", "c"] },
  { id: "sensitive-ballad", numerals: "vi – IV – I – V", steps: ["em", "c", "g", "d"] },
  { id: "doo-wop", numerals: "I – vi – IV – V", steps: ["g", "em", "c", "d"] },
  { id: "emotional-loop", numerals: "vi – iii – IV – I", steps: ["em", "bm", "c", "g"] },
];
